import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { MsfInput } from '@components/ui/forms/input/input';
import { AssignmentProcessService as AssignmentProcessApiService } from '@6grain/masofa-monolith-api-client/common/api/assignmentProcess.service';
import { CreateSubscriptionFromAssignmentRequest } from '@6grain/masofa-monolith-api-client/common/model/createSubscriptionFromAssignmentRequest';
import { QuartzScheduleFieldsComponent } from '@app/pages/admin/components/quartz-schedule-fields/quartz-schedule-fields';
import { defaultOrchestrationTimeZoneId } from '@app/pages/admin/orchestration-time-zones.util';
import { assignmentStartApiError } from '@app/pages/admin/assignment-start.util';

@Component({
  selector: 'app-make-subscription-dialog',
  standalone: true,
  imports: [
    TranslateModule,
    DialogModule,
    PrimaryButton,
    SecondaryButton,
    MsfInput,
    QuartzScheduleFieldsComponent,
  ],
  templateUrl: './make-subscription-dialog.html',
  styleUrl: './make-subscription-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MakeSubscriptionDialogComponent {
  private readonly assignments = inject(AssignmentProcessApiService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly failed = output<string>();

  readonly visible = signal(false);
  readonly saving = signal(false);
  readonly scheduleValid = signal(true);
  readonly name = signal('');
  readonly cronExpression = signal('0 0 6 * * ?');
  readonly timeZoneId = signal(defaultOrchestrationTimeZoneId());
  readonly canSave = computed(() =>
    !!this.name().trim()
    && !!this.cronExpression().trim()
    && !!this.timeZoneId().trim()
    && this.scheduleValid()
    && !this.saving());

  private assignmentId = '';

  open(assignmentId: string, defaultName: string): void {
    this.assignmentId = assignmentId;
    this.name.set(defaultName);
    this.cronExpression.set('0 0 6 * * ?');
    this.timeZoneId.set(defaultOrchestrationTimeZoneId());
    this.visible.set(true);
  }

  close(): void {
    this.visible.set(false);
  }

  setName(value: string): void {
    this.name.set(value);
  }

  setCronExpression(value: string): void {
    this.cronExpression.set(value);
  }

  setTimeZoneId(value: string): void {
    this.timeZoneId.set(value);
  }

  save(): void {
    const assignmentId = this.assignmentId;
    const name = this.name().trim();
    const cronExpression = this.cronExpression().trim();
    const timeZoneId = this.timeZoneId().trim();
    if (!assignmentId || !this.canSave()) {
      return;
    }
    this.saving.set(true);
    const request: CreateSubscriptionFromAssignmentRequest = {
      name,
      cronExpression,
      timeZoneId,
      isActive: true,
    };
    this.assignments.assignmentsAssignmentIdSubscriptionPost(assignmentId, request)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.saving.set(false)),
      )
      .subscribe({
        next: () => {
          this.visible.set(false);
          void this.router.navigate(['/admin/process-management/subscriptions']);
        },
        error: error => {
          this.failed.emit(assignmentStartApiError(error, 'Failed to create subscription from assignment'));
        },
      });
  }
}
